import React, { useState, useEffect } from "react";
// Phase 5+ — Avatar unifié pour tous les leaderboards/contributeurs.
// Remplace les <img src=picture/> custom par un fallback initiales colorées.
import { UserAvatar } from "./UserAvatar";
// INSTANCE PARTAGÉE obligatoire (bug prod 06/07/2026) : l'instance axios
// locale n'avait ni X-CSRF-Token (→ 403 systématique sur mark-displayed,
// toasts de badges re-servis à chaque session), ni Bearer natif Android,
// ni retry réseau, ni X-Client/X-App-Version. Cf. DUPLICATE_GUARDRAILS.
import { useAuth, api } from "../context/AuthContext";

// badge_icon stocké côté backend = nom d'icône lucide (ex: "Search",
// "PartyPopper"). On le convertit en emoji pour le toast (sinon le nom
// s'affichait tel quel en gros texte). Si c'est déjà un emoji, on garde.
const BADGE_ICON_EMOJI = {
  Search: "🔍", MessageCircle: "💬", Smile: "😊", PartyPopper: "🎉",
  Trophy: "🏆", Award: "🏅", Medal: "🏅", Star: "⭐", Sparkles: "✨",
  Heart: "❤️", Gift: "🎁", Zap: "⚡", Flame: "🔥", Crown: "👑",
  Rocket: "🚀", Target: "🎯", Users: "👥", ShoppingBag: "🛍️",
  ThumbsUp: "👍", Check: "✅", CheckCircle: "✅", Handshake: "🤝",
  Coins: "🪙", TrendingUp: "📈", Eye: "👁️", Camera: "📷",
};
const badgeIconToEmoji = (icon) => {
  if (!icon) return "🏆";
  // Déjà un emoji (présence de caractères non-ASCII) → on conserve.
  if (icon.codePointAt(0) > 127) return icon;
  return BADGE_ICON_EMOJI[icon] || "🏆";
};

// ============================================================
// GAMIFICATION NOTIFICATION CONTAINER
// Affiche les badges débloqués en temps réel (toast)
// ============================================================
export const GamificationNotificationContainer = () => {
  const [notifications, setNotifications] = useState([]);
  const { user } = useAuth();

  // Phase 8.4 (audit) — fix CRITIQUE : avant on appelait
  // /notifications?type=gamification qui pointe vers la collection
  // ``notifications`` standard (vide pour la gamification : les badges
  // sont écrits dans la collection ``gamification_notifications`` via
  // services.gamification_events). Conséquence : les badges débloqués
  // ne s'affichaient JAMAIS en toast → triggers Phase 6 dead code visuel.
  // → Bon endpoint : GET /api/users/me/notifications/gamification.
  // Marquage displayed après affichage pour ne pas re-pop le même toast.
  // L'endpoint exige une session : on ne poll donc QUE si l'utilisateur
  // est connecté (sinon 401 en boucle toutes les 60s pour les visiteurs).
  useEffect(() => {
    if (!user) return undefined;
    let displayedIds = new Set();
    const fetchAndShow = async () => {
      // Onglet en arrière-plan : inutile de poller (economie ressources
      // serveur + batterie). On reprendra au retour via visibilitychange.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await api.get("/users/me/notifications/gamification");
        const list = res?.data?.notifications || res?.data || [];
        // Filtre : non encore displayed côté backend ET pas déjà affiché
        // localement dans cette session.
        const fresh = list.filter(
          (n) => !n.displayed && !displayedIds.has(n.notification_id),
        );
        if (fresh.length === 0) return;
        // Affiche jusqu'à 3 toasts (5 empilés débordaient l'écran).
        const toShow = fresh.slice(0, 3);
        setNotifications(
          toShow.map((n) => {
            const rawIcon = n.data?.badge_icon || "";
            // badge_icon est souvent un NOM d'icône lucide ("Search",
            // "PartyPopper"…) -> s'affichait en texte géant. On le convertit
            // en emoji, et on retire ce nom s'il préfixe le message.
            const icon = badgeIconToEmoji(rawIcon);
            let message = n.message || n.body || "";
            if (rawIcon && message.startsWith(rawIcon + " ")) {
              message = message.slice(rawIcon.length + 1);
            }
            return { title: n.title, message, icon };
          }),
        );
        // Marque côté backend pour ne pas re-pop au prochain polling.
        const ids = toShow.map((n) => n.notification_id).filter(Boolean);
        ids.forEach((id) => displayedIds.add(id));
        if (ids.length > 0) {
          api.post("/users/me/notifications/gamification/mark-displayed",
                   { notification_ids: ids }).catch(() => {});
        }
        setTimeout(() => setNotifications([]), 6000);
      } catch (e) {
        // Silently ignore (user pas connecté ou endpoint indispo).
      }
    };
    // Fetch immédiat au mount + polling 3 min (au lieu de 60s : les
    // badges ne sont pas temps-réel, 3 min suffisent largement et on
    // divise par 3 la charge serveur).
    fetchAndShow();
    const interval = setInterval(fetchAndShow, 180000);
    // Au retour sur l'onglet, on rafraîchit tout de suite pour ne pas
    // attendre le prochain tick.
    const onVisible = () => { if (!document.hidden) fetchAndShow(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  if (notifications.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: "80px",
      right: "16px",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    }}>
      {notifications.map((notif, idx) => (
        <div key={idx} style={{
          background: "white",
          borderRadius: "12px",
          padding: "12px 16px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          animation: "slideIn 0.3s ease",
          maxWidth: "280px",
        }}>
          <span style={{ fontSize: "24px" }}>{notif.icon || "🏆"}</span>
          <div>
            <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: "#0f172a" }}>
              {notif.title || "Badge débloqué !"}
            </p>
            <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
              {notif.message || notif.body || ""}
            </p>
          </div>
        </div>
      ))}
      <style>{`@keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

// ============================================================
// USER BADGES PREVIEW
// Affiche les badges gagnés d'un utilisateur
// ============================================================
export const UserBadgesPreview = ({ userId, limit = 6 }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const endpoint = userId
          ? `/users/${userId}/gamification`
          : "/users/me/gamification";
        const res = await api.get(endpoint);
        const earned = (res.data?.badges || []).filter(b => b.status === "earned");
        setBadges(earned.slice(0, limit));
      } catch (e) {
        setBadges([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId, limit]);

  if (loading) return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#f1f5f9" }} />
      ))}
    </div>
  );

  if (badges.length === 0) return (
    <p style={{ color: "#94a3b8", fontSize: "13px", margin: 0 }}>Aucun badge pour l'instant</p>
  );

  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {badges.map((badge, idx) => (
        <div key={idx} title={`${badge.name} — ${badge.description}`} style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          background: badge.color || "#1DA1F2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          cursor: "help",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}>
          {getBadgeEmoji(badge.icon)}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// IMPACT DASHBOARD
// Tableau de bord gamification complet
// ============================================================
export const ImpactDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("badges");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/users/me/gamification");
        setData(res.data || null);
      } catch (e) {
        // Route gamification indisponible - affichage silencieux
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
      Chargement de vos stats...
    </div>
  );

  if (!data) return (
    <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>
      Impossible de charger les données
    </div>
  );

  const { stats, level, badges, earned_badges_count, total_badges_count } = data;
  const earnedBadges = (badges || []).filter(b => b.status === "earned");
  const lockedBadges = (badges || []).filter(b => b.status === "locked");

  const CATEGORIES = {
    acheteur: { label: "🛒 Acheteur", color: "#1DA1F2" },
    solidarite: { label: "💙 Solidarité", color: "#EC4899" },
    associations: { label: "🏛️ Associations", color: "#8B5CF6" },
    vendeur: { label: "🏷️ Vendeur", color: "#10B981" },
    mixte: { label: "🤝 Mixte", color: "#F59E0B" },
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      {/* Niveau + points */}
      <div style={{
        background: `linear-gradient(135deg, ${level?.current?.color || "#1DA1F2"}, #0d8ecf)`,
        borderRadius: "16px",
        padding: "24px",
        color: "white",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: "13px", opacity: 0.85 }}>Votre niveau</p>
            <h2 style={{ margin: 0, fontSize: "28px", fontWeight: "800" }}>
              {level?.current?.name || "Novice"}
            </h2>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ margin: "0 0 4px", fontSize: "13px", opacity: 0.85 }}>Points solidarité</p>
            <p style={{ margin: 0, fontSize: "28px", fontWeight: "800" }}>
              {level?.total_points || 0}
            </p>
          </div>
        </div>
        {/* Barre de progression */}
        {level?.next && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", opacity: 0.8, marginBottom: "6px" }}>
              <span>{level.current?.name}</span>
              <span>{level.points_to_next} pts pour {level.next?.name}</span>
            </div>
            <div style={{ background: "rgba(255,255,255,0.3)", borderRadius: "99px", height: "8px" }}>
              <div style={{
                background: "white",
                borderRadius: "99px",
                height: "8px",
                width: `${level.progress || 0}%`,
                transition: "width 0.5s ease",
              }} />
            </div>
          </>
        )}
      </div>

      {/* Stats rapides */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", marginBottom: "20px" }}>
        {[
          { label: "Achats", value: stats?.purchases_count || 0, icon: "🛒" },
          { label: "Ventes", value: stats?.sales_count || 0, icon: "🏷️" },
          { label: "Dons générés", value: `${(stats?.donations_generated || 0).toFixed(2)}€`, icon: "💙" },
          { label: "Badges", value: `${earned_badges_count || 0}/${total_badges_count || 0}`, icon: "🏆" },
        ].map((s, i) => (
          <div key={i} style={{
            background: "white",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}>
            <span style={{ fontSize: "28px" }}>{s.icon}</span>
            <div>
              <p style={{ margin: "0 0 2px", fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>{s.value}</p>
              <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Onglets badges */}
      <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9" }}>
          {[
            { id: "badges", label: `🏆 Gagnés (${earned_badges_count || 0})` },
            { id: "locked", label: `🔒 À débloquer (${lockedBadges.length})` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1,
              padding: "14px",
              border: "none",
              background: activeTab === tab.id ? "#f0f9ff" : "transparent",
              color: activeTab === tab.id ? "#1DA1F2" : "#64748b",
              fontWeight: activeTab === tab.id ? "700" : "400",
              fontSize: "13px",
              cursor: "pointer",
              borderBottom: activeTab === tab.id ? "2px solid #1DA1F2" : "2px solid transparent",
              transition: "all 0.15s",
            }}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px" }}>
          {/* Grouper par catégorie */}
          {Object.entries(CATEGORIES).map(([catId, cat]) => {
            const catBadges = (activeTab === "badges" ? earnedBadges : lockedBadges)
              .filter(b => b.category === catId);
            if (catBadges.length === 0) return null;
            return (
              <div key={catId} style={{ marginBottom: "24px" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: "14px", fontWeight: "700", color: cat.color }}>
                  {cat.label}
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "10px" }}>
                  {catBadges.map((badge, idx) => (
                    <div key={idx} style={{
                      borderRadius: "12px",
                      padding: "14px",
                      border: `1px solid ${activeTab === "badges" ? badge.color + "40" : "#e2e8f0"}`,
                      background: activeTab === "badges" ? badge.color + "08" : "#fafafa",
                      opacity: activeTab === "locked" ? 0.7 : 1,
                    }}>
                      <div style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        background: activeTab === "badges" ? badge.color : "#e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                        marginBottom: "8px",
                      }}>
                        {activeTab === "locked" ? "🔒" : getBadgeEmoji(badge.icon)}
                      </div>
                      <p style={{ margin: "0 0 4px", fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>
                        {badge.name}
                      </p>
                      <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#64748b", lineHeight: 1.4 }}>
                        {badge.description}
                      </p>
                      {activeTab === "badges" && badge.earned_level_name && (
                        <span style={{
                          background: badge.color,
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "99px",
                        }}>
                          {badge.earned_level_name}
                        </span>
                      )}
                      {activeTab === "locked" && badge.levels?.[0] && (
                        <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8" }}>
                          Objectif : {badge.levels[0].label}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {(activeTab === "badges" ? earnedBadges : lockedBadges).length === 0 && (
            <div style={{ textAlign: "center", padding: "30px", color: "#94a3b8" }}>
              {activeTab === "badges"
                ? "Aucun badge gagné pour l'instant. Continuez à utiliser Gively !"
                : "Tous les badges sont débloqués ! 🎉"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MONTHLY CHALLENGES
// ============================================================
export const MonthlyChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/challenges/monthly");
        setChallenges(res.data?.challenges || res.data || []);
      } catch (e) {
        setChallenges([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div style={{ color: "#94a3b8", padding: "20px", textAlign: "center" }}>Chargement...</div>;
  if (challenges.length === 0) return (
    <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>
      <p style={{ fontSize: "32px", margin: "0 0 8px" }}>🎯</p>
      <p>Défis du mois à venir</p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {challenges.map((c, idx) => (
        <div key={idx} style={{
          background: "white",
          borderRadius: "12px",
          padding: "16px",
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
            <div>
              <p style={{ margin: "0 0 4px", fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                {c.icon || "🎯"} {c.name || c.title}
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>{c.description}</p>
            </div>
            <span style={{
              background: "#f0fdf4",
              color: "#10B981",
              fontSize: "11px",
              fontWeight: "700",
              padding: "3px 8px",
              borderRadius: "99px",
              whiteSpace: "nowrap",
              marginLeft: "8px",
            }}>
              +{c.points || 0} pts
            </span>
          </div>
          <div style={{ background: "#f1f5f9", borderRadius: "99px", height: "6px" }}>
            <div style={{
              background: "#1DA1F2",
              borderRadius: "99px",
              height: "6px",
              width: `${Math.min((c.progress || 0) / (c.target || 1) * 100, 100)}%`,
              transition: "width 0.5s ease",
            }} />
          </div>
          <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8" }}>
            {c.progress || 0} / {c.target || 0}
          </p>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// WEEKLY IMPACT
// ============================================================
export const WeeklyImpact = () => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/stats/weekly-impact");
        setImpact(res.data);
      } catch (e) { setImpact(null); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div style={{ background: "linear-gradient(135deg, #1DA1F2,#0d8ecf)", borderRadius: "20px", padding: "24px", minHeight: "180px" }} />;
  if (!impact) return null;

  return (
    <div style={{ background: "linear-gradient(135deg, #1DA1F2, #0d8ecf)", borderRadius: "20px", padding: "24px", color: "white", boxShadow: "0 8px 30px rgba(29,161,242,0.3)" }}>
      <div style={{ marginBottom: "20px" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: "18px", fontWeight: "800" }}>⚡ Impact Gively</h3>
        <p style={{ margin: 0, fontSize: "13px", opacity: 0.85 }}>
          Notre communauté depuis le lancement
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", marginBottom: "16px" }}>
        {[
          { label: "Ventes solidaires", value: impact.sales_count || 0, icon: "🏷️" },
          { label: "Dons générés", value: `${(impact.total_donated || 0).toFixed(0)}€`, icon: "💙" },
          { label: "Associations partenaires", value: impact.total_associations ?? impact.associations_supported ?? 0, icon: "🏛️" },
          { label: "Membres", value: impact.total_members ?? impact.new_users ?? 0, icon: "👥" },
        ].map((s, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.15)", borderRadius: "12px", padding: "14px", backdropFilter: "blur(4px)" }}>
            <div style={{ fontSize: "22px", marginBottom: "6px" }}>{s.icon}</div>
            <div style={{ fontSize: "24px", fontWeight: "900", lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: "12px", opacity: 0.85, marginTop: "4px" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {impact.top_contributor && (
        <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "14px", padding: "14px", display: "flex", alignItems: "center", gap: "12px", marginBottom: impact.top_association ? "10px" : "0" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "900", flexShrink: 0, border: "2px solid rgba(255,255,255,0.5)", overflow: "hidden" }}>
            <UserAvatar user={impact.top_contributor} size="md" className="w-full h-full" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", opacity: 0.85, marginBottom: "2px" }}>🏆 Top contributeur</div>
            <div style={{ fontSize: "15px", fontWeight: "800" }}>{impact.top_contributor.name}</div>
            <div style={{ fontSize: "12px", opacity: 0.85 }}>{impact.top_contributor.donations_count} don{impact.top_contributor.donations_count > 1 ? "s" : ""} réalisé{impact.top_contributor.donations_count > 1 ? "s" : ""}</div>
          </div>
          <div style={{ fontSize: "11px", background: "rgba(255,255,255,0.2)", padding: "5px 10px", borderRadius: "99px", cursor: "pointer" }}>
            Afficher profil →
          </div>
        </div>
      )}

      {impact.top_association && (
        <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: "14px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ fontSize: "22px" }}>🏛️</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", opacity: 0.85, marginBottom: "2px" }}>Association la plus soutenue</div>
            <div style={{ fontSize: "14px", fontWeight: "700" }}>{impact.top_association.name}</div>
          </div>
          <div style={{ fontSize: "16px", fontWeight: "800" }}>{impact.top_association.total_donated}€</div>
        </div>
      )}
    </div>
  );
};


// ============================================================
// LEADERBOARD
// ============================================================
export const Leaderboard = ({ limit = 10 }) => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/leaderboard?period=${period}&limit=${limit}`);
        setLeaders(res.data?.leaderboard || res.data?.leaders || []);
      } catch (e) {
        setLeaders([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [period, limit]);

  const MEDALS = ["🥇", "🥈", "🥉"];

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto" }}>
      {/* Onglets période */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        {[
          { id: "week", label: "Cette semaine" },
          { id: "month", label: "Ce mois" },
          { id: "all", label: "Tout temps" },
        ].map(p => (
          <button key={p.id} onClick={() => setPeriod(p.id)} style={{
            padding: "8px 16px",
            borderRadius: "99px",
            border: "none",
            background: period === p.id ? "#1DA1F2" : "#f1f5f9",
            color: period === p.id ? "white" : "#64748b",
            fontWeight: period === p.id ? "700" : "400",
            fontSize: "13px",
            cursor: "pointer",
          }}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
          Chargement...
        </div>
      ) : leaders.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
          <p style={{ fontSize: "36px", margin: "0 0 8px" }}>🏆</p>
          <p>Aucun classement disponible</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {leaders.map((leader, idx) => (
            <div key={idx} style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 16px",
              background: idx < 3 ? `${["#FFF9E6", "#F5F5F5", "#FFF3E6"][idx]}` : "white",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}>
              <span style={{ fontSize: "22px", width: "28px", textAlign: "center", flexShrink: 0 }}>
                {MEDALS[idx] || `#${idx + 1}`}
              </span>
              <div style={{
                width: "36px", height: "36px", borderRadius: "50%",
                background: "#1DA1F2", display: "flex", alignItems: "center",
                justifyContent: "center", color: "white", fontWeight: "700",
                fontSize: "14px", flexShrink: 0, overflow: "hidden",
              }}>
                {/* Phase 5+ — UserAvatar unifié (fallback initiales colorées). */}
                <UserAvatar user={leader} size="sm" className="w-full h-full" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 2px", fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                  {leader.name || "Utilisateur"}
                </p>
                <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                  {leader.sales_count || 0} vente{(leader.sales_count || 0) > 1 ? "s" : ""} • {leader.associations_supported || 0} asso{(leader.associations_supported || 0) > 1 ? "s" : ""}
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ margin: "0 0 2px", fontWeight: "800", fontSize: "16px", color: "#1DA1F2" }}>
                  {typeof leader.score === "number" ? Math.round(leader.score) : (leader.points || 0)}
                </p>
                <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>pts</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// HOME GAMIFICATION WIDGET — Style Subway Surfers
// Podium top 3 + liste déroulante + ta position + stats semaine
// ============================================================
export const GamificationHomeWidget = () => {
  const [leaders, setLeaders] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [weekStats, setWeekStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [lbRes, statsRes] = await Promise.all([
          api.get("/leaderboard?period=week&category=donations"),
          api.get("/stats/weekly-impact").catch(() => ({ data: null })),
        ]);
        const lb = lbRes.data?.leaderboard || lbRes.data?.leaders || [];
        setLeaders(lb);
        setWeekStats(statsRes.data);

        // Trouver ma position. NOTE : l'auth Gively passe par des cookies httpOnly
        // (pas de token JS). L'ancien `getToken()` n'existait PAS → ReferenceError
        // avalée par le catch global → le rang perso n'était JAMAIS calculé. On
        // appelle directement /auth/me (renvoie 401 si non connecté → catché).
        {
          try {
            const meRes = await api.get("/auth/me");
            const meId = meRes.data?.user_id;
            if (meId) {
              const idx = lb.findIndex(l => l.user_id === meId);
              if (idx >= 0) {
                setMyRank({ rank: idx + 1, ...lb[idx] });
              } else {
                // Pas dans le top — récupérer mes stats quand même
                try {
                  const gamRes = await api.get("/users/me/gamification");
                  const pts = gamRes.data?.level?.total_points || 0;
                  setMyRank({ rank: null, name: meRes.data?.name, picture: meRes.data?.picture, points: pts, score: 0 });
                } catch (_) {}
              }
            }
          } catch (_) {}
        }
      } catch (e) {
        setLeaders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const PODIUM_COLORS = [
    { bg: "linear-gradient(135deg, #FFD700, #FFA500)", border: "#FFD700", shadow: "rgba(255,215,0,0.25)", text: "#92600a" },
    { bg: "linear-gradient(135deg, #C0C0C0, #A0A0A0)", border: "#B0B0B0", shadow: "rgba(160,160,160,0.25)", text: "#555" },
    { bg: "linear-gradient(135deg, #CD7F32, #B8690E)", border: "#CD7F32", shadow: "rgba(205,127,50,0.25)", text: "#6b3a0a" },
  ];

  const LEVEL_COLORS = {
    "Novice": "#94a3b8", "Bronze": "#CD7F32", "Argent": "#A0A0A0", "Or": "#d4a017", "Platine": "#7c3aed"
  };

  if (loading) return (
    <div className="rounded-2xl overflow-hidden border border-slate-200" style={{ background: "linear-gradient(135deg, #f8fafc, #f0f4ff, #f0fdf4)", padding: "24px", minHeight: "280px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
        <span style={{ fontSize: "24px" }}>🏆</span>
        <div style={{ height: "20px", width: "200px", background: "#e2e8f0", borderRadius: "10px" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: "12px" }}>
        {[80, 100, 60].map((h, i) => (
          <div key={i} style={{ width: "80px", height: `${h}px`, background: "#e2e8f0", borderRadius: "12px 12px 0 0" }} />
        ))}
      </div>
    </div>
  );

  if (leaders.length === 0) return (
    <div className="rounded-2xl border border-slate-200" style={{ background: "linear-gradient(135deg, #f8fafc, #f0f4ff)", padding: "32px", textAlign: "center" }}>
      <p style={{ fontSize: "48px", margin: "0 0 12px" }}>🏆</p>
      <h3 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>Classement de la semaine</h3>
      <p style={{ margin: 0, fontSize: "14px", color: "#94a3b8" }}>Le classement commence bientôt !</p>
    </div>
  );

  const top3 = leaders.slice(0, 3);
  const rest = leaders.slice(3);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const podiumHeights = top3.length >= 3 ? [100, 130, 80] : top3.length === 2 ? [100, 130] : [130];
  const podiumRanks = top3.length >= 3 ? [2, 1, 3] : top3.length === 2 ? [2, 1] : [1];

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200" style={{ background: "white", position: "relative" }}>
      {/* Header gradient */}
      <div style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #e0f2fe 50%, #f0fdf4 100%)", padding: "20px 20px 0", position: "relative" }}>
        {/* Particules décoratives */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "hidden", pointerEvents: "none" }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{
              position: "absolute",
              width: `${6 + i * 3}px`, height: `${6 + i * 3}px`,
              borderRadius: "50%",
              background: i % 2 === 0 ? "rgba(255,215,0,0.2)" : "rgba(29,161,242,0.12)",
              top: `${8 + i * 18}%`, left: `${3 + i * 20}%`,
              animation: `float ${3 + i}s ease-in-out infinite alternate`,
            }} />
          ))}
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "28px" }}>🏆</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "900", color: "#0f172a", letterSpacing: "-0.3px" }}>
                  Classement de la semaine
                </h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                  Top contributeurs solidaires
                </p>
              </div>
            </div>
            {weekStats && (
              <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "12px", padding: "6px 12px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#059669" }}>
                  {weekStats.total_donations_count || 0}
                </div>
                <div style={{ fontSize: "10px", color: "#6ee7b7", fontWeight: "600" }}>dons cette semaine</div>
              </div>
            )}
          </div>

          {/* Podium */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: "8px", paddingBottom: "0" }}>
            {podiumOrder.map((leader, i) => {
              const rank = podiumRanks[i];
              const colors = PODIUM_COLORS[rank - 1];
              const height = podiumHeights[i];
              const isFirst = rank === 1;
              return (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, maxWidth: isFirst ? "130px" : "110px" }}>
                  {/* Avatar */}
                  <div style={{ position: "relative", marginBottom: "8px" }}>
                    {isFirst && (
                      <div style={{ position: "absolute", top: "-18px", left: "50%", transform: "translateX(-50%)", fontSize: "22px", zIndex: 2 }}>👑</div>
                    )}
                    <div style={{
                      width: isFirst ? "64px" : "50px", height: isFirst ? "64px" : "50px",
                      borderRadius: "50%",
                      border: `3px solid ${colors.border}`,
                      boxShadow: `0 4px 14px ${colors.shadow}`,
                      overflow: "hidden",
                      background: "#f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <UserAvatar user={leader} size={isFirst ? "lg" : "md"} className="w-full h-full" />
                    </div>
                    <div style={{
                      position: "absolute", bottom: "-6px", left: "50%", transform: "translateX(-50%)",
                      width: "22px", height: "22px", borderRadius: "50%",
                      background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11px", fontWeight: "900", color: "white",
                      boxShadow: `0 2px 6px ${colors.shadow}`,
                    }}>{rank}</div>
                  </div>

                  {/* Name */}
                  <p style={{ margin: "4px 0 2px", fontSize: "12px", fontWeight: "700", color: "#0f172a", textAlign: "center", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {leader.name || "Anonyme"}
                  </p>

                  {/* Score */}
                  <p style={{ margin: "0 0 6px", fontSize: "11px", color: colors.text, fontWeight: "800" }}>
                    {typeof leader.score === "number" ? leader.score.toFixed(0) : leader.score} pts
                  </p>

                  {/* Level badge */}
                  {leader.level && (
                    <span style={{
                      fontSize: "9px", fontWeight: "700",
                      padding: "2px 8px", borderRadius: "99px",
                      background: `${LEVEL_COLORS[leader.level.name] || "#94a3b8"}15`,
                      color: LEVEL_COLORS[leader.level.name] || "#94a3b8",
                      border: `1px solid ${LEVEL_COLORS[leader.level.name] || "#94a3b8"}30`,
                      marginBottom: "8px",
                    }}>{leader.level.name}</span>
                  )}

                  {/* Podium bar */}
                  <div style={{
                    width: "100%", height: `${height}px`,
                    background: colors.bg, borderRadius: "12px 12px 0 0",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
                    paddingTop: "12px",
                    boxShadow: `0 -2px 12px ${colors.shadow}`,
                  }}>
                    <span style={{ fontSize: isFirst ? "28px" : "22px" }}>
                      {["🥇", "🥈", "🥉"][rank - 1]}
                    </span>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "white", opacity: 0.9, marginTop: "4px", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                      {leader.sales_count || 0} vente{(leader.sales_count || 0) > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section basse */}
      <div style={{ padding: "16px 20px", background: "white" }}>
        {/* Mon rang */}
        {myRank && (
          <div style={{
            display: "flex", alignItems: "center", gap: "12px",
            background: "#eff6ff", border: "1px solid #bfdbfe",
            borderRadius: "12px", padding: "12px 14px", marginBottom: "12px",
          }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "50%",
              background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "11px", fontWeight: "900", color: "#1d4ed8", flexShrink: 0,
            }}>
              {myRank.rank ? `#${myRank.rank}` : "—"}
            </div>
            <div style={{
              width: "32px", height: "32px", borderRadius: "50%", overflow: "hidden",
              background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <UserAvatar user={myRank} size="sm" className="w-full h-full" />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: "#1d4ed8" }}>
                Vous — {myRank.name}
              </p>
              <p style={{ margin: 0, fontSize: "11px", color: "#60a5fa" }}>
                {myRank.rank ? `${myRank.score?.toFixed?.(0) || 0} pts d'impact` : `${myRank.points || 0} pts solidarité`}
              </p>
            </div>
            <span style={{ fontSize: "14px" }}>💪</span>
          </div>
        )}

        {/* Bouton expand */}
        {rest.length > 0 && (
          <button onClick={() => setExpanded(!expanded)} style={{
            width: "100%", padding: "10px",
            background: expanded ? "#f8fafc" : "white",
            border: "1px solid #e2e8f0", borderRadius: "12px",
            color: "#64748b", fontSize: "13px", fontWeight: "600",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            transition: "all 0.2s",
          }}>
            {expanded ? "Masquer le classement" : `Voir tout le classement (${leaders.length} joueurs)`}
            <span style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s", fontSize: "16px" }}>▾</span>
          </button>
        )}

        {/* Liste étendue */}
        {expanded && (
          <div style={{
            marginTop: "12px", display: "flex", flexDirection: "column", gap: "4px",
            maxHeight: "400px", overflowY: "auto",
          }}>
            {leaders.map((leader, idx) => {
              const isMe = myRank && leader.user_id === myRank.user_id;
              const isTop3 = idx < 3;
              return (
                <div key={idx} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", borderRadius: "10px",
                  background: isMe ? "#eff6ff" : isTop3 ? "#fffbeb" : "#fafafa",
                  border: isMe ? "1px solid #bfdbfe" : "1px solid #f1f5f9",
                  transition: "all 0.15s",
                }}>
                  <div style={{
                    width: "28px", fontSize: isTop3 ? "18px" : "12px", fontWeight: "800",
                    color: isTop3 ? ["#d4a017", "#71717a", "#b45309"][idx] : "#94a3b8",
                    textAlign: "center", flexShrink: 0,
                  }}>
                    {isTop3 ? ["🥇", "🥈", "🥉"][idx] : `#${idx + 1}`}
                  </div>

                  <div style={{
                    width: "34px", height: "34px", borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                    background: isTop3 ? `${["#fef3c7", "#f1f5f9", "#fef3c7"][idx]}` : "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: isMe ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                  }}>
                    <UserAvatar user={leader} size="sm" className="w-full h-full" />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: isMe ? "#1d4ed8" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {leader.name || "Anonyme"} {isMe && "(Vous)"}
                    </p>
                    <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>
                      {leader.sales_count || 0} vente{(leader.sales_count || 0) > 1 ? "s" : ""} • {leader.associations_supported || 0} asso{(leader.associations_supported || 0) > 1 ? "s" : ""}
                    </p>
                  </div>

                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ margin: 0, fontSize: "14px", fontWeight: "900", color: isTop3 ? ["#d4a017", "#71717a", "#b45309"][idx] : "#1DA1F2" }}>
                      {typeof leader.score === "number" ? leader.score.toFixed(0) : leader.score}€
                    </p>
                    <p style={{ margin: 0, fontSize: "10px", color: "#cbd5e1" }}>dons</p>
                  </div>
                </div>
              );
            })}

            {myRank && !myRank.rank && (
              <>
                <div style={{ textAlign: "center", padding: "6px", color: "#cbd5e1", fontSize: "16px", letterSpacing: "4px" }}>• • •</div>
                <div style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", borderRadius: "10px",
                  background: "#eff6ff", border: "1px solid #bfdbfe",
                }}>
                  <div style={{ width: "28px", fontSize: "12px", fontWeight: "800", color: "#94a3b8", textAlign: "center" }}>—</div>
                  <div style={{ width: "34px", height: "34px", borderRadius: "50%", overflow: "hidden", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #3b82f6" }}>
                    <UserAvatar user={myRank} size="sm" className="w-full h-full" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: "#1d4ed8" }}>{myRank.name} (Vous)</p>
                    <p style={{ margin: 0, fontSize: "11px", color: "#60a5fa" }}>Pas encore dans le top — continuez !</p>
                  </div>
                  <span style={{ fontSize: "18px" }}>🚀</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Stats Gively (cumul depuis le lancement) */}
        {weekStats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "14px" }}>
            {[
              { icon: "🏷️", value: weekStats.sales_count || 0, label: "ventes" },
              { icon: "🏛️", value: weekStats.total_associations ?? weekStats.associations_supported ?? 0, label: "assos" },
              { icon: "👥", value: weekStats.total_members ?? weekStats.new_users ?? 0, label: "membres" },
            ].map((s, i) => (
              <div key={i} style={{
                background: "#f8fafc", borderRadius: "10px", padding: "10px 8px",
                textAlign: "center", border: "1px solid #e2e8f0",
              }}>
                <div style={{ fontSize: "18px", marginBottom: "2px" }}>{s.icon}</div>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a" }}>{s.value}</div>
                <div style={{ fontSize: "10px", color: "#94a3b8", fontWeight: "500" }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes float {
          from { transform: translateY(0px) scale(1); }
          to { transform: translateY(-8px) scale(1.15); }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// HELPER - Emoji selon nom icône
// ============================================================
const getBadgeEmoji = (iconName) => {
  const map = {
    ShoppingCart: "🛒", ShoppingBag: "🛍️", Heart: "❤️", HeartHandshake: "🤝",
    Gift: "🎁", Crown: "👑", Users: "👥", Star: "⭐", BookmarkPlus: "🔖",
    Building2: "🏛️", Package: "📦", Camera: "📷", Archive: "📦", Tag: "🏷️",
    Timer: "⏱️", Repeat2: "🔄", MessageCircle: "💬", MessageSquare: "💬",
    UserCheck: "✅", Smile: "😊", PartyPopper: "🎉", Clock: "⏰", Cake: "🎂",
    MapPin: "📍", Bookmark: "🔖", Search: "🔍", Zap: "⚡", Bookmark: "🔖",
  };
  return map[iconName] || "🏆";
};
